import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { Router } from "@angular/router";
import { ButtonComponent } from "@optee/ui/components/button/button.component";
import { CircleComponent } from "@optee/ui/components/circle/circle.component";
import { ProjectsComponent } from "@optee/ui/components/projects/projects.component";
import { ExtendedCardComponent } from "../components/cards/extended-card/extended-card.component";
import { IconCardComponent } from "../components/cards/icon-card/icon-card.component";
import { LivrableCardComponent } from "../components/cards/livrable-card/livrable-card.component";
import { OpteeAdvantagesCardComponent } from "../components/cards/optee-advantages-card/optee-advantages-card.component";
import { RedirectCardComponent } from "../components/cards/redirect-card/redirect-card.component";
import { ContactFormComponent } from "../components/contact-form/contact-form.component";
import { FeatureSectionComponent } from "../components/feature-section/feature-section.component";
import { HeroSectionHorizontalComponent } from "../components/hero-section-horizontal/hero-section-horizontal.component";
import { QuestionsComponent } from "../components/questions/questions.component";

@Component({
  selector: "swc-ai-data-page",
  template: `
    <div class="relative block overflow-hidden">
      <oui-circle
        class="-right-[70px] top-[172px] z-10 hidden w-[244px] lg:block"
        theme="light"
      />
      <swc-hero-section-horizontal [multipleImg]="images">
        <div title>
          Définissez votre stratégie de rénovation énergétique avec intelligence
          et simplicité
        </div>
      </swc-hero-section-horizontal>
    </div>
    <div
      class="max-w-app mx-auto flex flex-col justify-between gap-20 px-6 py-8 lg:flex-row lg:gap-32 lg:px-44 lg:py-20"
    >
      <swc-feature-section
        class="w-full lg:max-w-lg"
        label="Potentiel bâtiments"
        title="Récupérez instantanément les données de vos bâtiments"
      >
        <div class="flex flex-col gap-6">
          <p>
            Avec une adresse, notre traitons plus de 100 données fiables pour
            vous fournir des informations clés sur votre bâtiment.
          </p>
          <p>
            Nous vous accompagnons pour comprendre ses performances, identifiez
            des axes d'amélioration et optimisez votre stratégie de rénovation
            énergétique.
          </p>

          <oui-button href="/demo" variant="primary">
            Analyser mon bâtiment
          </oui-button>
        </div>
      </swc-feature-section>

      <div class="flex flex-col gap-6">
        <swc-extended-card icon="heater">
          <span class="font-semibold">Les caractéristiques techniques</span>
          de votre bâtiment.
        </swc-extended-card>
        <swc-extended-card icon="flash">
          <span class="font-semibold">Les consommations énergétiques</span>
          , pour identifier les gisements d’économies.
        </swc-extended-card>
        <swc-extended-card icon="dpe">
          <span class="font-semibold">Les DPE et audits</span>
          pour évaluer sa conformité et prioriser les actions.
        </swc-extended-card>
        <span class="text-xs text-gray-600">
          Sources : BDNB, Enedis, GRDF, l’ADEME.
        </span>
      </div>
    </div>
    <div class="bg-white">
      <div
        class="max-w-app mx-auto flex flex-col items-center justify-between gap-20 px-6 py-8 lg:px-44 lg:py-20 xl:flex-row xl:gap-32"
      >
        <div class="flex flex-1 flex-col gap-6 md:flex-row">
          <swc-icon-card
            class="shadow-o2 rounded-3xl"
            icon="impact"
            title="Impact énergétique"
          >
            <p class="p-soft">
              Nous utilisons des règles de calculs issues de bureaux d’études
              pour simuler vos consommations après travaux.
            </p>
          </swc-icon-card>
          <swc-icon-card
            class="shadow-o2 rounded-3xl"
            icon="roi"
            title="ROI potentiel"
          >
            <p class="p-soft">
              Nous simulons et comparons la rentabilité de chaque projet en
              fonction des économies d’énergie réalisées.
            </p>
          </swc-icon-card>
        </div>
        <swc-feature-section
          class="w-full lg:max-w-lg"
          label="Scénario de rénovation"
          title="Générez des scénarios de rénovation énergétique"
        >
          <div class="flex flex-col gap-6">
            <p>
              Accédez à une liste d’opérations calibrée selon les critères de
              votre bâtiment.
            </p>
            <p>
              Chaque intervention est optimisée avec des règles de calcul
              expertes pour maximiser vos économies et votre impact énergétique.
            </p>

            <oui-button href="/demo" variant="primary">
              Créer mon scénario de rénovation
            </oui-button>
          </div>
        </swc-feature-section>
      </div>
    </div>
    <div class="bg-primary-700">
      <div
        class="max-w-app relative mx-auto flex flex-col gap-16 overflow-hidden px-6 py-8 lg:px-44 lg:py-20"
      >
        <swc-feature-section
          display="horizontal"
          label="Analyse instantanée"
          theme="dark"
          title="Analysez et comparez chaque opération pour optimiser vos décisions"
        >
          <p>
            Prenez des décisions éclairées grâce à des estimations claires :
            coût des travaux, subventions disponibles, impact carbone,
            rentabilité projetée. Chaque opération est notée pour garantir une
            exécution sans surprise.
          </p>
        </swc-feature-section>

        <div class="flex flex-col justify-center gap-6 lg:flex-row">
          <swc-optee-advantages-card image="type-operation">
            Adapter votre projet à
            <b>vos besoins spécifiques.</b>
          </swc-optee-advantages-card>
          <swc-optee-advantages-card image="cout-subventions">
            Maximiser l’accès aux
            <b>aides et subventions disponibles.</b>
          </swc-optee-advantages-card>
          <swc-optee-advantages-card image="integrer-operation">
            Déterminez les
            <b>opérations les plus rentables.</b>
          </swc-optee-advantages-card>
        </div>
      </div>
    </div>
    <div
      class="max-w-app mx-auto flex flex-col flex-wrap justify-between gap-20 px-6 py-8 md:gap-10 lg:flex-row lg:px-44 lg:py-20"
    >
      <swc-feature-section
        class="w-full lg:flex-1"
        label="Livrables conformes"
        title="Produisez automatiquement des livrables complets et conformes aux normes réglementaires"
      >
        <div class="flex flex-col gap-6">
          <p>
            Simplifiez vos démarches grâce à la génération automatique de
            documents techniques et réglementaires (briefs techniques, cahiers
            des charges, études de faisabilité et avant-projets).
          </p>
          <oui-button href="/demo" variant="primary">
            Simuler mon projet
          </oui-button>
        </div>
      </swc-feature-section>

      <div
        class="flex flex-1 flex-wrap items-center justify-center gap-6 md:min-w-[600px]"
      >
        <swc-livrable-card icon="list">
          <b>Description détaillée</b>
          <br />
          de l’opération avec critères et contraintes.
        </swc-livrable-card>
        <swc-livrable-card icon="recherche">
          <b>Estimation des coûts,</b>
          <br />
          des économies et impact énergétique.
        </swc-livrable-card>
        <swc-livrable-card icon="calendrier">
          <b>Planning</b>
          <br />
          prévisionnel clair.
        </swc-livrable-card>
        <swc-livrable-card icon="optimisation">
          <b>Optimisation des aides</b>
          <br />
          et subventions disponibles.
        </swc-livrable-card>
      </div>
    </div>
    <div class="bg-primary-700 relative overflow-hidden">
      <oui-circle class="-right-[123px] top-[130px] w-[398px]" theme="light" />
      <oui-circle
        class="-left-[165px] bottom-[305px] w-[365px]"
        theme="light"
      />
      <div
        class="max-w-app mx-auto flex flex-col gap-6 px-6 py-8 lg:gap-20 lg:px-44 lg:py-20"
      >
        <oui-projects theme="dark" />
        <swc-contact-form />
        <div
          class="item-center flex flex-col justify-between gap-20 md:py-16 lg:flex-row"
        >
          <div
            class="flex flex-col justify-center gap-6 text-white lg:max-w-2xl"
          >
            <h2 class="font-display text-[38px] font-semibold">
              Vous avez un projet de rénovation énergétique à lancer ?
            </h2>
            <p>
              Accédez à notre réseau de professionnels certifiés, recevez des
              devis et suivez vos projets directement depuis notre plateforme
            </p>
          </div>
          <swc-redirect-card
            class="mx-auto"
            name="Appel d'offres et gestion opérationnelle"
            icon="expertise"
            link="tendering"
            type="tendering"
            (navigate)="navigateToTendering($event)"
          />
        </div>
      </div>
    </div>
    <swc-questions theme="light" />
  `,
  imports: [
    QuestionsComponent,
    ProjectsComponent,
    HeroSectionHorizontalComponent,
    FeatureSectionComponent,
    ExtendedCardComponent,
    LivrableCardComponent,
    CircleComponent,
    ContactFormComponent,
    RedirectCardComponent,
    OpteeAdvantagesCardComponent,
    IconCardComponent,
    ButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class AiDataPageComponent {
  private readonly router = inject(Router);

  images = [
    {
      class: "bottom-0 right-10 w-[528px]",
      alt: "illustration ia data; rentabilité",
      src: "assets/hero-section/chatbot2.png",
    },
    {
      class: "bottom-64 z-10 right-72 w-[70px]",
      alt: "illustration ia data; coût",
      src: "assets/hero-section/cout.png",
    },
    {
      class: "bottom-64 z-10 right-52 w-[70px]",
      alt: "illustration ia data; subvention",
      src: "assets/hero-section/subventions.png",
    },
    {
      class: "bottom-64 z-10 right-32 w-[70px]",
      alt: "illustration ia data; subvention ",
      src: "assets/hero-section/subventions2.png",
    },
    {
      class: "bottom-28 right-8 w-[208px]",
      alt: "illustration ia data; impact énergétique prévisionnel",
      src: "assets/hero-section/impact.png",
    },
    {
      class: "bottom-56 right-64 w-[319px]",
      alt: "illustration ia data; opération",
      src: "assets/hero-section/operation_line.png",
    },
  ];

  navigateToTendering(link: string) {
    this.router.navigate([`/${link}`]).then(() => {
      // eslint-disable-next-line @rx-angular/prefer-no-layout-sensitive-apis
      window.scrollTo(0, 0); // Scroll to top
    });
  }
}
